/**
 * ImageBannerSection — renders the image banner using the full new config schema.
 */

import React from 'react';

const CLIP_PATH_MAP = {
  'none':           'none',
  'diagonal-left':  'polygon(0 0, 100% 0, 100% 85%, 0 100%)',
  'diagonal-right': 'polygon(0 0, 100% 0, 100% 100%, 0 85%)',
  'curve-bottom':   'ellipse(100% 85% at 50% 0%)',
  'parallelogram':  'polygon(8% 0, 100% 0, 92% 100%, 0% 100%)',
};

function ctaBtnStyle(cta, theme) {
  const s = cta.style || 'filled';
  const sz = cta.size || 'md';
  const padding = sz === 'sm' ? '8px 16px' : sz === 'lg' ? '14px 28px' : '10px 22px';
  const fontSize = sz === 'sm' ? 13 : sz === 'lg' ? 17 : 15;
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding, fontSize, fontWeight: 600,
    textDecoration: 'none', borderRadius: 6,
    border: '2px solid transparent', cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.15s',
  };
  if (s === 'filled')   return { ...base, background: cta.color || (theme?.primary || '#7c3aed'), color: cta.textColor || '#fff', borderColor: cta.color || (theme?.primary || '#7c3aed') };
  if (s === 'outlined') return { ...base, background: 'transparent', color: cta.textColor || '#fff', borderColor: cta.color || '#fff' };
  if (s === 'ghost')    return { ...base, background: 'rgba(255,255,255,0.12)', color: cta.textColor || '#fff', borderColor: 'transparent' };
  // link
  return { ...base, background: 'transparent', color: cta.textColor || '#fff', borderColor: 'transparent', textDecoration: 'underline', padding: '8px 4px' };
}

export default function ImageBannerSection({ config, theme }) {
  const cfg = config || {};

  // ── Image background ─────────────────────────────────────────────────────────
  const bgStyle = {};
  if (cfg.imageUrl) {
    const bgPos = cfg.imagePosition === 'custom'
      ? `${cfg.imagePositionX ?? 50}% ${cfg.imagePositionY ?? 50}%`
      : cfg.imagePosition || 'center';
    bgStyle.backgroundImage    = `url(${cfg.imageUrl})`;
    bgStyle.backgroundSize     = cfg.imageFit === 'contain'    ? 'contain'
                               : cfg.imageFit === 'fill'       ? '100% 100%'
                               : cfg.imageFit === 'none'       ? 'auto'
                               : cfg.imageFit === 'scale-down' ? 'contain'
                               : 'cover';
    bgStyle.backgroundPosition = bgPos;
    bgStyle.backgroundRepeat   = cfg.imageRepeat || 'no-repeat';
  }

  // ── Height / aspect ratio ────────────────────────────────────────────────────
  const aspectRatioMap = { '16:9': '16/9', '4:3': '4/3', '3:2': '3/2', '1:1': '1/1', '21:9': '21/9' };
  const aspectRatio = cfg.aspectRatio === 'custom' && cfg.customAspectRatioX && cfg.customAspectRatioY
    ? `${cfg.customAspectRatioX}/${cfg.customAspectRatioY}`
    : (aspectRatioMap[cfg.aspectRatio] || undefined);

  const height = cfg.aspectRatio && cfg.aspectRatio !== 'none' ? 'auto'
    : cfg.height === 'viewport' ? '100vh'
    : cfg.height === 'px'      ? `${cfg.heightPx || 400}px`
    : undefined;

  const minHeight = !aspectRatio && !height ? (cfg.imageUrl ? '200px' : '120px') : undefined;

  // ── Border radius ────────────────────────────────────────────────────────────
  const borderRadius = cfg.borderRadiusPerCorner
    ? `${cfg.borderRadiusTopLeft || 0}px ${cfg.borderRadiusTopRight || 0}px ${cfg.borderRadiusBottomRight || 0}px ${cfg.borderRadiusBottomLeft || 0}px`
    : `${cfg.borderRadius || 0}px`;

  // ── Box shadow ───────────────────────────────────────────────────────────────
  const boxShadow = cfg.boxShadow === 'sm' ? '0 1px 4px rgba(0,0,0,0.12)'
    : cfg.boxShadow === 'md' ? '0 4px 16px rgba(0,0,0,0.15)'
    : cfg.boxShadow === 'lg' ? '0 8px 32px rgba(0,0,0,0.18)'
    : cfg.boxShadow === 'custom'
      ? `${cfg.boxShadowX || 0}px ${cfg.boxShadowY || 8}px ${cfg.boxShadowBlur || 24}px ${cfg.boxShadowSpread || 0}px ${cfg.boxShadowColor || '#000'}40`
    : 'none';

  // ── Clip path ────────────────────────────────────────────────────────────────
  const clipPath = cfg.clipPath === 'custom'
    ? (cfg.clipPathCustomValue || 'none')
    : (CLIP_PATH_MAP[cfg.clipPath] || 'none');

  // ── Overlay ──────────────────────────────────────────────────────────────────
  let overlayBg = '';
  let overlayPatternStyle = {};
  if (cfg.overlayVisible) {
    if (cfg.overlayType === 'gradient') {
      overlayBg = cfg.overlayGradientType === 'radial'
        ? `radial-gradient(circle, ${cfg.overlayGradientFrom || '#000'}, ${cfg.overlayGradientTo || '#7c3aed'})`
        : `linear-gradient(${cfg.overlayGradientAngle || 135}deg, ${cfg.overlayGradientFrom || '#000'}, ${cfg.overlayGradientTo || '#7c3aed'})`;
    } else if (cfg.overlayType === 'pattern') {
      overlayPatternStyle = {
        backgroundImage: `radial-gradient(circle, ${cfg.overlayColor || '#000'}55 1.5px, transparent 1.5px)`,
        backgroundSize: '14px 14px',
      };
    } else {
      overlayBg = cfg.overlayColor || '#000';
    }
  }

  // ── Text overlay position ────────────────────────────────────────────────────
  const pad = cfg.textPadding || 24;
  const textPosMap = {
    'centered':      { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' },
    'bottom-left':   { bottom: pad, left: pad },
    'bottom-center': { bottom: pad, left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: pad, right: pad },
    'top-left':      { top: pad, left: pad },
    'top-center':    { top: pad, left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: pad, right: pad },
    'center-left':   { top: '50%', left: pad, transform: 'translateY(-50%)' },
    'center-right':  { top: '50%', right: pad, transform: 'translateY(-50%)' },
    'custom':        { top: `${cfg.textOverlayPositionY || 50}%`, left: `${cfg.textOverlayPositionX || 50}%`, transform: 'translate(-50%,-50%)' },
  };
  const textPosStyle = textPosMap[cfg.textOverlayLayout] || textPosMap['centered'];
  const textAlign = cfg.textAlignment || 'center';
  const btnJustify = textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start';

  // ── Hover / transition ───────────────────────────────────────────────────────
  const hoverTransition = `transform ${cfg.hoverTransitionDuration || 300}ms ease, box-shadow ${cfg.hoverTransitionDuration || 300}ms ease`;

  // ── Banner wrapper ───────────────────────────────────────────────────────────
  const wrapperStyle = {
    position: 'relative',
    overflow: 'hidden',
    display: 'block',
    width: '100%',
    maxWidth: cfg.maxWidth !== 'none' && cfg.maxWidthPx ? `${cfg.maxWidthPx}px` : undefined,
    margin: cfg.sectionWidth === 'narrow' || cfg.sectionWidth === 'contained' ? '0 auto' : undefined,
    marginTop:    cfg.marginTop    ? `${cfg.marginTop}px`    : undefined,
    marginBottom: cfg.marginBottom ? `${cfg.marginBottom}px` : undefined,
    paddingTop:    cfg.paddingTop    ? `${cfg.paddingTop}px`    : undefined,
    paddingBottom: cfg.paddingBottom ? `${cfg.paddingBottom}px` : undefined,
    paddingLeft:   cfg.paddingLeft   ? `${cfg.paddingLeft}px`   : undefined,
    paddingRight:  cfg.paddingRight  ? `${cfg.paddingRight}px`  : undefined,
    height,
    minHeight,
    aspectRatio,
    borderRadius,
    border: cfg.borderVisible ? `${cfg.borderWidth || 1}px ${cfg.borderStyle || 'solid'} ${cfg.borderColor || '#e2e8f0'}` : undefined,
    boxShadow,
    clipPath: clipPath !== 'none' ? clipPath : undefined,
    cursor: cfg.bannerLinkUrl ? 'pointer' : (cfg.cursorStyle !== 'default' ? cfg.cursorStyle : undefined),
    transition: hoverTransition,
    textDecoration: 'none',
    ...bgStyle,
  };

  const bannerContent = (
    <>
      {/* Overlay layer */}
      {cfg.overlayVisible && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: overlayBg || undefined,
          opacity: (cfg.overlayOpacity ?? 40) / 100,
          mixBlendMode: cfg.overlayBlendMode || 'normal',
          ...overlayPatternStyle,
        }} />
      )}

      {/* Text overlay */}
      {cfg.textOverlayVisible && (
        <div style={{
          position: 'absolute', zIndex: 2,
          maxWidth: cfg.textMaxWidth ? `${cfg.textMaxWidth}px` : '640px',
          textAlign,
          ...textPosStyle,
        }}>
          {cfg.badgeVisible && cfg.badgeText && (
            <div style={{
              display: 'inline-block', marginBottom: 12,
              fontSize: 12, fontWeight: 700,
              padding: '4px 14px', borderRadius: 20,
              background: cfg.badgeColor || '#7c3aed', color: '#fff',
            }}>
              {cfg.badgeText}
            </div>
          )}

          <h2 style={{
            fontWeight: cfg.headlineFontWeight || 700,
            fontSize: cfg.headlineFontSize || 40,
            color: cfg.headlineColor || '#fff',
            fontFamily: cfg.headlineFontFamily ? `"${cfg.headlineFontFamily}", sans-serif` : 'inherit',
            lineHeight: 1.2,
            margin: '0 0 12px',
            textShadow: cfg.textShadow
              ? `0 2px ${cfg.textShadowBlur || 8}px ${cfg.textShadowColor || '#000'}80`
              : 'none',
          }}>
            {cfg.headlineText || 'Your headline here'}
          </h2>

          {cfg.subheadlineVisible && cfg.subheadlineText && (
            <p style={{
              fontSize: cfg.subheadlineFontSize || 18,
              color: cfg.subheadlineColor || '#e2e8f0',
              margin: '0 0 20px',
              lineHeight: 1.55,
              textShadow: cfg.textShadow
                ? `0 1px ${Math.round((cfg.textShadowBlur || 8) * 0.6)}px ${cfg.textShadowColor || '#000'}60`
                : 'none',
            }}>
              {cfg.subheadlineText}
            </p>
          )}

          {cfg.ctaVisible && (
            <div style={{
              display: 'flex', gap: 12, flexWrap: 'wrap',
              marginTop: cfg.ctaMarginTop || 24,
              flexDirection: cfg.ctaLayout === 'stacked' ? 'column' : 'row',
              justifyContent: btnJustify,
              alignItems: cfg.ctaLayout === 'stacked' ? btnJustify : 'center',
            }}>
              {cfg.primaryCTA && (
                <a href={cfg.primaryCTA.url || '#'}
                  target={cfg.primaryCTA.openNewTab ? '_blank' : undefined}
                  rel={cfg.primaryCTA.openNewTab ? 'noreferrer' : undefined}
                  style={ctaBtnStyle(cfg.primaryCTA, theme)}>
                  {cfg.primaryCTA.icon && cfg.primaryCTA.iconPosition !== 'right' && (
                    <i className={`bi ${cfg.primaryCTA.icon}`} />
                  )}
                  {cfg.primaryCTA.label || 'Get Started'}
                  {cfg.primaryCTA.icon && cfg.primaryCTA.iconPosition === 'right' && (
                    <i className={`bi ${cfg.primaryCTA.icon}`} />
                  )}
                </a>
              )}
              {cfg.secondaryCTA?.visible && (
                <a href={cfg.secondaryCTA.url || '#'}
                  target={cfg.secondaryCTA.openNewTab ? '_blank' : undefined}
                  rel={cfg.secondaryCTA.openNewTab ? 'noreferrer' : undefined}
                  style={ctaBtnStyle(cfg.secondaryCTA, theme)}>
                  {cfg.secondaryCTA.label || 'Learn More'}
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Overlay-positioned caption */}
      {cfg.captionVisible && cfg.captionText && cfg.captionPosition === 'overlay-bottom' && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3,
          padding: '8px 16px', background: 'rgba(0,0,0,0.45)',
          fontSize: cfg.captionFontSize || 13,
          color: cfg.captionColor || '#e2e8f0',
          textAlign: cfg.captionAlignment || 'center',
        }}>
          {cfg.captionText}
        </div>
      )}

      {/* Shape divider — bottom */}
      {cfg.shapeDividerBottom && cfg.shapeDividerBottom !== 'none' && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3, lineHeight: 0 }}>
          {cfg.shapeDividerBottom === 'wave' && (
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: cfg.shapeDividerHeight || 60, display: 'block' }}>
              <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill={cfg.shapeDividerColor || '#fff'} />
            </svg>
          )}
          {cfg.shapeDividerBottom === 'diagonal' && (
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: cfg.shapeDividerHeight || 60, display: 'block' }}>
              <polygon points="0,60 1440,0 1440,60" fill={cfg.shapeDividerColor || '#fff'} />
            </svg>
          )}
          {cfg.shapeDividerBottom === 'curve' && (
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: cfg.shapeDividerHeight || 60, display: 'block' }}>
              <ellipse cx="720" cy="60" rx="720" ry="60" fill={cfg.shapeDividerColor || '#fff'} />
            </svg>
          )}
          {cfg.shapeDividerBottom === 'arrow' && (
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: cfg.shapeDividerHeight || 60, display: 'block' }}>
              <polygon points="0,0 720,60 1440,0 1440,60 0,60" fill={cfg.shapeDividerColor || '#fff'} />
            </svg>
          )}
        </div>
      )}
    </>
  );

  // If the whole banner is a link
  if (cfg.bannerLinkUrl) {
    return (
      <div>
        <a href={cfg.bannerLinkUrl}
          target={cfg.bannerLinkOpenNewTab ? '_blank' : undefined}
          rel={cfg.bannerLinkOpenNewTab ? 'noreferrer' : undefined}
          aria-label={cfg.ariaLabel || undefined}
          id={cfg.id || undefined}
          className={cfg.customClassName || undefined}
          style={wrapperStyle}>
          {bannerContent}
        </a>
        {cfg.captionVisible && cfg.captionText && cfg.captionPosition === 'below' && (
          <p style={{ fontSize: cfg.captionFontSize || 13, color: cfg.captionColor || '#64748b', textAlign: cfg.captionAlignment || 'center', margin: '8px 0 0' }}>
            {cfg.captionText}
          </p>
        )}
        {cfg.creditVisible && cfg.creditText && (
          <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', margin: '4px 8px 0' }}>
            {cfg.creditUrl
              ? <a href={cfg.creditUrl} target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>{cfg.creditText}</a>
              : cfg.creditText}
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        role={cfg.role !== 'none' ? cfg.role : 'presentation'}
        aria-label={cfg.ariaLabel || undefined}
        id={cfg.id || undefined}
        className={cfg.customClassName || undefined}
        style={wrapperStyle}>
        {bannerContent}
      </div>
      {cfg.captionVisible && cfg.captionText && cfg.captionPosition === 'below' && (
        <p style={{ fontSize: cfg.captionFontSize || 13, color: cfg.captionColor || '#64748b', textAlign: cfg.captionAlignment || 'center', margin: '8px 0 0' }}>
          {cfg.captionText}
        </p>
      )}
      {cfg.creditVisible && cfg.creditText && (
        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', margin: '4px 8px 0' }}>
          {cfg.creditUrl
            ? <a href={cfg.creditUrl} target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>{cfg.creditText}</a>
            : cfg.creditText}
        </p>
      )}
    </div>
  );
}
